import { createContext, useContext, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  LayoutDashboard, Server, HardDrive, CalendarClock, ListChecks,
  RotateCcw, Settings, Database, LogOut, ShieldAlert, Menu,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/sources", key: "sources", icon: Server, testid: "nav-sources" },
  { to: "/destinations", key: "destinations", icon: HardDrive, testid: "nav-destinations" },
  { to: "/plans", key: "plans", icon: CalendarClock, testid: "nav-plans" },
  { to: "/runs", key: "runs", icon: ListChecks, testid: "nav-runs" },
  { to: "/restore", key: "restore", icon: RotateCcw, testid: "nav-restore" },
  { to: "/settings", key: "settings", icon: Settings, testid: "nav-settings" },
];

const SidebarCtx = createContext({ openSidebar: () => {} });

function SidebarBody({ onNavigate }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  return (
    <div className="h-full flex flex-col bg-[#0A0A0A] text-white">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10 shrink-0">
        <div className="h-8 w-8 bg-[#0052FF] flex items-center justify-center">
          <Database className="h-4 w-4 text-white" strokeWidth={2.5} />
        </div>
        <span className="font-display font-black tracking-tight">MARIABACKUP</span>
      </div>
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors duration-150",
                isActive
                  ? "bg-[#0052FF] text-white"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              )
            }
          >
            <n.icon className="h-4 w-4" />
            {t(`nav.${n.key}`)}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 shrink-0">
        <div className="flex items-center gap-3 px-2 py-2">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 object-cover" />
          ) : (
            <div className="h-8 w-8 bg-white/10 flex items-center justify-center text-xs font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-wide text-[#0052FF]">{user?.role}</p>
          </div>
          <LanguageSwitcher />
          <button
            data-testid="logout-button"
            onClick={logout}
            className="text-white/50 hover:text-destructive transition-colors"
            title={t("common.signOut")}
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);

  return (
    <SidebarCtx.Provider value={{ openSidebar: () => setOpen(true) }}>
      <div className="min-h-screen flex bg-background">
        {/* Desktop sidebar */}
        <aside className="w-60 shrink-0 hidden md:flex fixed h-screen z-40" data-testid="desktop-sidebar">
          <SidebarBody />
        </aside>

        {/* Mobile drawer */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="left"
            className="p-0 w-72 max-w-[80vw] border-r-0 text-white [&>button]:text-white/70 [&>button]:hover:text-white"
            data-testid="mobile-sidebar"
          >
            <SidebarBody onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Main */}
        <main className="flex-1 md:ml-60 min-h-screen w-full min-w-0">
          {children}
        </main>
      </div>
    </SidebarCtx.Provider>
  );
}

export function PageHeader({ title, subtitle, actions, badge }) {
  const { openSidebar } = useContext(SidebarCtx);
  return (
    <div className="bg-card border-b-2 border-[#1A1A1A] sticky top-0 z-30 px-4 md:px-8 py-3 md:py-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
      <div className="flex items-center gap-2 md:gap-3 min-w-0">
        <button
          type="button"
          onClick={openSidebar}
          className="md:hidden shrink-0 -ml-1 p-1.5 text-foreground hover:text-brand transition-colors"
          data-testid="mobile-menu-button"
          aria-label="Open navigation menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="font-display font-bold text-xl md:text-2xl tracking-tight truncate">{title}</h1>
            {badge}
          </div>
          {subtitle && <p className="text-xs md:text-sm text-muted-foreground mt-0.5 truncate">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap justify-end ml-auto">{actions}</div>}
    </div>
  );
}

export function LogicalBackupNotice() {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-2 border border-border bg-secondary px-4 py-3 text-xs text-muted-foreground">
      <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-yellow-600" />
      <span>
        <strong className="text-foreground">{t("layout.noticeLead")}</strong> {t("layout.noticeBody1")}{" "}
        <code>--single-transaction</code>{" "}
        {t("layout.noticeBody2")}
      </span>
    </div>
  );
}
